import { Card } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'

import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'

import { useFormDialog, useHttpError } from '../../../hooks'

import moment from 'moment'
import { useSelector } from 'react-redux'
import { EnumCommodity, EnumDivisa, EnumUnidadMedida } from '../../../api'
import { NewPriceModelAPI } from '../../../api/Argentia/endpoints/pricing/model/prices'
import { EnumPeriodoCalculo } from '../../../api/oData/enums/enumPeriodoCalculo'
import { EnumPeriodoLiquidacion } from '../../../api/oData/enums/enumPeriodoLiquidacion'
import { EnumPriceType } from '../../../api/oData/enums/enumPriceType'
import { PriceExpressionValidator } from '../../../components'
import { validatePriceExpression } from '../../../components/shared/priceExpressionValidator/priceExpressionValidator.helper'
import { enumToSelectOptionArray, toLocaleDate } from '../../../helpers'
import { NonAuthRoutes } from '../../../model'
import { getCustomerDomain } from '../../../redux'
import {
  selectActiveContract,
  selectClientContracts,
  selectIsFetchingClientContracts
} from '../../../redux/selectors/client.selectors'
import {
  contractModelToFlexibilityOptionsMapper,
  contractsListMapper
} from '../../clients/client.mapper'
import {
  contractProductAPIModelListToOptionsMapper,
  ProductSelectOption
} from '../../contracts/contracts.mapper'
import {
  fetchProductsByContractID,
  fetchProductsBySettlementGroupID
} from '../../contracts/contracts.service'
import { PriceDetails } from '../../pricing/pricing.Model'
import {
  calculationConceptTypeToSelectOptionMapper,
  postFlexibilityMapper,
  priceDetailsToPostFlexibilityRequest
} from '../flexibility.mapper'
import { CalculationTypeSelectOption, NewFlexibility } from '../flexibility.Model'
import {
  fetchCalculationConceptTypesForProduct,
  fetchFlexibilityGroups,
  fetchFlexibilitySubtypesForCalculationTypes,
  fetchInvoiceCycles,
  postFlexibility
} from '../flexibility.service'

export interface FlexibilityAddPageProps {
  location: {
    state: {
      contractID?: number
      settlementGroupId?: number
      settlementGroupDescription?: string
      commodity: EnumCommodity
    }
  }
}

export const FlexibilityAddPage: React.FC<any> = props => {
  const history = useHistory()
  const [t] = useTranslation(['flexibilityAdd', 'globalerisLibrary', 'enums'])

  const { info, error } = useNotify()

  const fetchingContracts = useSelector(selectIsFetchingClientContracts)
  const contractsData = contractsListMapper(useSelector(selectClientContracts))
  const contractData = useSelector(selectActiveContract)
  const contractsOptions = contractModelToFlexibilityOptionsMapper(contractsData ?? [])

  const commodity = props.location.state.commodity as EnumCommodity
  const activeContractID: number | undefined = props.location?.state?.contractID
  const activeSettlementGroupId: number | undefined = props.location?.state?.settlementGroupId
  const activeSettlementGroupDesc: string = props.location?.state?.settlementGroupDescription ?? ''

  const [formData, setFormData] = useState<Partial<NewFlexibility>>({
    contractId: activeContractID,
    settlementGroupId: activeSettlementGroupId
  })
  const [price, setPrice] = useState<PriceDetails>({} as PriceDetails)
  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [productsOptions, setProductsOptions] = useState<SelectOption[]>([])
  const [isFetchingProductsOptions, setIsFetchingProductsOptions] = useState<boolean>(false)

  const [minDate, setMinDate] = useState<string | null>(null)
  const [maxDate, setMaxDate] = useState<string | null>(null)
  const [dateInvalid, setDateInvalid] = useState<boolean>(false)
  const [dateSignInvalid, setDateSignInvalid] = useState<boolean>(false)

  const [flexibilityTypeOptions, setFlexibilityTypeOptions] = useState<SelectOption[]>([])
  const [isFetchingTypeOptions, setIsFetchingTypeOptions] = useState<boolean>(false)

  const [flexibilitySubtypesOptions, setFlexibilitySubtypesOptions] = useState<SelectOption[]>([])
  const [isFetchingSubtypesOptions, setIsFetchingSubtypesOptions] = useState<boolean>(false)

  const [flexibilityGroupsOptions, setFlexibilityGroupsOptions] = useState<SelectOption[]>([])
  const [isFetchingGroupsOptions, setIsFetchingGroupsOptions] = useState<boolean>(false)

  const [invoiceCyclesOptions, setInvoiceCyclesOptions] = useState<SelectOption[]>([])
  const [isFetchingInvoiceCyclesOptions, setIsFetchingInvoiceCyclesOptions] =
    useState<boolean>(false)

  const [expressionValidated, setExpressionValidated] = useState<boolean>(false)
  const [isPriceValid, setIsPriceValid] = useState<boolean>(false)
  const [percentage, setPercentage] = useState<number>(0)

  const { showError, clearError, errorProps } = useHttpError()
  const { openForm, closeForm, formProps, CustomFormDialog } = useFormDialog()

  // Form Definition Starts

  const fetchProductsForContract = async (contractID: number) => {
    setIsFetchingProductsOptions(true)
    await fetchProductsByContractID(contractID).then(response => {
      if (response.success) {
        setProductsOptions(contractProductAPIModelListToOptionsMapper(response.data ?? []))
      }
      setIsFetchingProductsOptions(false)
    })
  }

  const fetchProductsForSettlementGroup = async (settlementGroupId: number) => {
    setIsFetchingProductsOptions(true)
    await fetchProductsBySettlementGroupID(settlementGroupId).then(response => {
      if (response.success) {
        setProductsOptions(contractProductAPIModelListToOptionsMapper(response.data ?? []))
      }
      setIsFetchingProductsOptions(false)
    })
  }

  const fetchCalculationTypesOptions = async (productID: number) => {
    setIsFetchingTypeOptions(true)
    await fetchCalculationConceptTypesForProduct(productID).then(response => {
      if (response.success) {
        setFlexibilityTypeOptions(calculationConceptTypeToSelectOptionMapper(response.data ?? []))
      }
      setIsFetchingTypeOptions(false)
    })
  }

  const fetchFlexibilitySubtypesOptions = async (calculationConceptType: number) => {
    setIsFetchingSubtypesOptions(true)
    await fetchFlexibilitySubtypesForCalculationTypes([calculationConceptType]).then(response => {
      if (response.success) {
        setFlexibilitySubtypesOptions(response.data ?? [])
      }
      setIsFetchingSubtypesOptions(false)
    })
  }

  const fetchFlexibilityGroupsOptions = async () => {
    setIsFetchingGroupsOptions(true)
    await fetchFlexibilityGroups().then(response => {
      if (response.success) {
        setFlexibilityGroupsOptions(response.data ?? [])
      }
      setIsFetchingGroupsOptions(false)
    })
  }

  const fetchInvoiceCyclesOptions = async () => {
    setIsFetchingInvoiceCyclesOptions(true)
    await fetchInvoiceCycles().then(response => {
      if (response.success) {
        setInvoiceCyclesOptions(response.data ?? [])
      }
      setIsFetchingInvoiceCyclesOptions(false)
    })
  }

  const checkDates = (startDate, endDate) => {
    let startDateValid = true
    let endDateValid = true

    if (!moment(startDate).isValid() && !moment(endDate).isValid()) {
    } else if (moment(startDate).isValid() && !moment(endDate).isValid()) {
      startDateValid = minDate ? moment(startDate).isSameOrAfter(moment(minDate), 'days') : true
    } else if (!moment(startDate).isValid() && moment(endDate).isValid()) {
      endDateValid = maxDate ? moment(endDate).isSameOrBefore(moment(maxDate), 'days') : true
    } else if (moment(startDate).isValid() && moment(endDate).isValid()) {
      if (moment(startDate).isSameOrBefore(moment(endDate), 'days')) {
        startDateValid = minDate ? moment(startDate).isSameOrAfter(moment(minDate), 'days') : true
        endDateValid = maxDate ? moment(endDate).isSameOrBefore(moment(maxDate), 'days') : true
      } else {
        startDateValid = false
        endDateValid = false
      }
    }
    const isValid = startDateValid && endDateValid
    setDateInvalid(!isValid)
  }

  const validateSignDate = (signDate, startDate) => {
    if (signDate && startDate && moment(signDate).isAfter(moment(startDate), 'days')) {
      setDateSignInvalid(true)
    } else {
      setDateSignInvalid(false)
    }
  }

  const addFormSections: Section[] = [
    {
      inputs: [
        {
          name: 'contractId',
          label: t('flexibilityAdd:forms.add.inputs.contract'),
          type: DataType.SELECT,
          selectOptions: contractsOptions,
          isLoading: fetchingContracts,
          readOnly: !!activeContractID,
          display: !activeSettlementGroupId,
          required: !activeSettlementGroupId,
          onChange: (value, formState) => {
            fetchProductsForContract(+value)
          }
        },
        {
          name: 'settlementGroupId',
          label: t('flexibilityAdd:forms.add.inputs.settlementGroup'),
          type: DataType.SELECT,
          selectOptions: activeSettlementGroupId
            ? [{ value: activeSettlementGroupId, label: activeSettlementGroupDesc }]
            : [],
          readOnly: !!activeSettlementGroupId,
          display: !!activeSettlementGroupId
        },
        {
          name: 'productId',
          label: t('flexibilityAdd:forms.add.inputs.product'),
          type: DataType.SELECT,
          selectOptions: productsOptions,
          isLoading: isFetchingProductsOptions,
          helpText: !activeSettlementGroupId ? 'Seleccione un Contrato' : '',
          onChange: (value, formState) => {
            fetchCalculationTypesOptions(+value)
            const product = productsOptions.find(
              option => option.value === +value
            ) as ProductSelectOption
            !activeSettlementGroupId && setMinDate(moment(product.startDate).format('YYYY-MM-DD'))
            !activeSettlementGroupId && setMaxDate(moment(product.endDate).format('YYYY-MM-DD'))
            setFormData(prevState => ({
              ...prevState,
              productId: +value,
              productCode: product.code,
              startDate: !activeSettlementGroupId ? product.startDate : prevState.startDate,
              endDate: !activeSettlementGroupId ? product.endDate : prevState.startDate
            }))
          }
        },
        {
          name: 'description',
          label: t('flexibilityAdd:forms.add.inputs.description'),
          type: DataType.TEXT
        },
        {
          name: 'calculationType',
          label: t('flexibilityAdd:forms.add.inputs.flexibilityType'),
          type: DataType.SELECT,
          selectOptions: flexibilityTypeOptions,
          isLoading: isFetchingTypeOptions,
          required: true,
          onChange: (value, formState) => {
            fetchFlexibilitySubtypesOptions(+value)
            const type = flexibilityTypeOptions.find(
              option => option.value === +value
            ) as CalculationTypeSelectOption
            setFormData(prevState => ({
              ...prevState,
              flexibilityType: +value,
              currency: type.currency,
              measureUnit: type.measureUnit
            }))
          }
        },
        {
          name: 'subtype',
          label: t('flexibilityAdd:forms.add.inputs.subtype'),
          type: DataType.SELECT,
          selectOptions: flexibilitySubtypesOptions,
          isLoading: isFetchingSubtypesOptions,
          helpText: 'Seleccione el tipo de flexibilidad',
          required: true
        },
        {
          name: 'flexibilityGroupId',
          label: t('flexibilityAdd:forms.add.inputs.group'),
          type: DataType.SELECT,
          selectOptions: flexibilityGroupsOptions,
          isLoading: isFetchingGroupsOptions,
          onChange: (value, formState) => {
            setFormData(prevState => ({
              ...prevState,
              flexibilityGroupId: +value,
              flexibilityGroup: flexibilityGroupsOptions.find(option => option.value === +value)
                ?.label
            }))
          }
        },
        {
          name: 'startDate',
          label: t('flexibilityAdd:forms.add.inputs.startDate'),
          type: DataType.DATE,
          minDate: minDate ?? undefined,
          maxDate:
            formData.endDate && moment(formData.endDate).isValid()
              ? moment(formData.endDate).format('YYYY-MM-DD')
              : undefined,
          errorText: minDate
            ? `La fecha debe encontrarse dentro de la vigencia del producto (Inicio: ${toLocaleDate(
                moment(minDate).toDate()
              )}).`
            : '',
          helpText: minDate
            ? `La fecha debe encontrarse dentro de la vigencia del producto (Inicio: ${toLocaleDate(
                moment(minDate).toDate()
              )}).`
            : '',
          required: true,
          onBlur: (value: any) => {
            if (moment(value).isValid()) {
              setFormData(prevState => ({
                ...prevState,
                startDate: moment(value).toDate()
              }))
              checkDates(value, formData.endDate)
              validateSignDate(formData.signDate, formData.startDate)
            }
          }
        },
        {
          name: 'endDate',
          label: t('flexibilityAdd:forms.add.inputs.endDate'),
          type: DataType.DATE,
          minDate:
            formData.startDate && moment(formData.startDate).isValid()
              ? moment(formData.startDate).format('YYYY-MM-DD')
              : undefined,
          maxDate: maxDate ?? undefined,
          errorText: maxDate
            ? `La fecha debe encontrarse dentro de la vigencia del producto (Fin: ${toLocaleDate(
                moment(maxDate).toDate()
              )}).`
            : '',
          helpText: maxDate
            ? `La fecha debe encontrarse dentro de la vigencia del producto (Fin: ${toLocaleDate(
                moment(maxDate).toDate()
              )}).`
            : '',
          required: true,
          onBlur: (value: any) => {
            if (moment(value).isValid()) {
              setFormData(prevState => ({
                ...prevState,
                endDate: moment(value).toDate()
              }))
              checkDates(formData.startDate, value)
              validateSignDate(formData.signDate, formData.startDate)
            }
          }
        },
        {
          name: 'signDate',
          label: t('flexibilityAdd:forms.add.inputs.signDate'),
          type: DataType.DATE,
          maxDate: moment(formData.startDate).isValid()
            ? moment(formData.startDate).format('YYYY-MM-DD')
            : (undefined ?? undefined),
          errorText: formData.startDate
            ? `La fecha debe ser igual o anterior a la fecha de inicio (${toLocaleDate(
                moment(formData.startDate).toDate()
              )}).`
            : '',
          required: true,
          errorState: dateSignInvalid,
          onBlur: (value: any) => {
            if (moment(value).isValid()) {
              setFormData(prevState => ({
                ...prevState,
                signDate: moment(value).toDate()
              }))
              validateSignDate(formData.signDate, formData.startDate)
            }
          }
        },
        {
          name: 'calculationPeriod',
          label: t('flexibilityAdd:forms.add.inputs.calculationPeriod'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumPeriodoCalculo, 'enums:EnumPeriodoCalculo'),
          required: true
        },
        {
          name: 'settlementPeriod',
          label: t('flexibilityAdd:forms.add.inputs.settlementPeriod'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumPeriodoLiquidacion,
            'enums:EnumPeriodoLiquidacion'
          ),
          required: true
        },
        {
          name: 'InvoicingCycleId',
          label: t('flexibilityAdd:forms.add.inputs.invoicingCycle'),
          type: DataType.SELECT,
          clearable: true,
          selectOptions: invoiceCyclesOptions,
          isLoading: isFetchingInvoiceCyclesOptions
        },
        {
          name: 'currency',
          label: t('flexibilityAdd:forms.add.inputs.currency'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumDivisa, 'enums:CurrencySigns'),
          readOnly: true,
          required: true
        },
        {
          name: 'measureUnit',
          label: t('flexibilityAdd:forms.add.inputs.measureUnit'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumUnidadMedida, 'enums:UnitMeasure'),
          readOnly: true,
          required: false
        },
        {
          name: 'comments',
          label: t('flexibilityAdd:forms.add.inputs.comments'),
          type: DataType.TEXT
        }
      ]
    }
  ]

  const onSave = async data => {
    if (!data) {
      history.goBack()
      return false
    }

    const newPrice = priceDetailsToPostFlexibilityRequest(price)
    const subTypeDescription = flexibilitySubtypesOptions.find(
      option => option.value === data.subtype
    )?.label
    const priceCode = data.contractId
      ? `${data.contractId}_${Math.floor(Math.random() * (data.contractId || activeSettlementGroupId || 1000))}`
      : `G${activeSettlementGroupId}_F${Math.floor(Math.random() * activeSettlementGroupId! * 1000)}_${Math.floor(Math.random() * activeSettlementGroupId! * 1000)}`
    const priceToPost: NewPriceModelAPI = {
      ...newPrice,
      code: priceCode,
      description: subTypeDescription
    }

    const request = {
      ...postFlexibilityMapper(formData as NewFlexibility),
      price: priceToPost
    }

    await postFlexibility(request).then(response => {
      if (response.success) {
        info(t('flexibilityAdd:notifications.save.success'))
        response?.data?.id
          ? history.push(NonAuthRoutes.flexibilityEdit, {
              flexibilityId: response.data.id,
              settlementGroupId: activeSettlementGroupId,
              settlementGroupCode: activeSettlementGroupDesc
            })
          : history.goBack()
      } else {
        error(t('flexibilityAdd:notifications.save.error'))
        return false
      }
      return true
    })
    return true
  }

  //Form config Starts
  const options: FormOptions = {
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('flexibilityAdd:forms.add.analytics')
    },
    saveDisabled:
      dateInvalid || dateSignInvalid || !isPriceValid || !expressionValidated ? true : undefined,
    onSave: onSave
  }

  // Form Config Ends

  // Handlers Starts

  // Handlers Ends

  // Hooks Starts

  useEffect(() => {
    // fetchFlexibilityDetailsById(props.location.state.flexibilityId)
    fetchFlexibilityGroupsOptions()
    fetchInvoiceCyclesOptions()
    activeContractID && fetchProductsForContract(activeContractID)
    activeSettlementGroupId && fetchProductsForSettlementGroup(activeSettlementGroupId)
  }, [])

  const handleSelect = (key: string, value: any) => {
    setPrice(prevState => {
      const newPrice = { ...prevState, [key]: value } as PriceDetails
      validatePriceExpression(newPrice, setIsPriceValid)
      return newPrice
    })
  }

  // Hooks Ends

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={false} error={errorProps}>
      <PageHeader title={t('flexibilityAdd:title')}>
        {/* <Button
          // className={classes.cardAction}
          // variant="contained"
          onClick={() => {
            history.goBack()
          }}
          size="small"
          color="primary"
        >
          {t('flexibilityAdd:mainActions.back')}
        </Button> */}
        {/* <ButtonWithPermissions
          variant="contained"
          onClick={onSave}
          size="small"
          color="primary"
          hasPermission={hasPermission(Resources.Flexibility, [UserPermission.EDIT])}
        >
          {t('flexibilityAdd:mainActions.save')}
        </ButtonWithPermissions> */}
      </PageHeader>
      <Card raised>
        <Form
          title={'Nueva Flexibilidad'}
          sections={addFormSections}
          options={options}
          data={formData}
        />
        <PriceExpressionValidator
          showPriceUnit
          showVersion
          showRounding
          title={t('flexibilityAdd:forms.add.priceSectionTitle')}
          priceType={EnumPriceType.Formula}
          commodity={commodity}
          onIsValid={isValid => {
            setExpressionValidated(isValid)
          }}
          onSelectValidExpression={expression => handleSelect('priceExpression', expression)}
          onSelectPriceCalculationType={calculationType =>
            handleSelect('priceCalculationType', calculationType)
          }
          onSelectPriceUnit={priceUnit => handleSelect('precioUnitario', priceUnit)}
          onSelectRounding={rounding => handleSelect('redondeo', rounding)}
          priceVersion={1}
        />
      </Card>
    </Page>
  )
}
